import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/input";
import { LocationCountry, LocationRegion, LocationCity, LocationSeoCreateDto, LocationSeoUpdateDto } from "../../modules/shared/model";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { 
  createLocationSeo, 
  updateLocationSeo, 
  fetchLocationSeosByCountry,
  fetchLocationSeosByRegion,
  fetchLocationSeosByCity,
  clearLocationSeoError
} from "../../redux/Slices/LocationSeoSlice";
import { Loader2 } from "lucide-react";

interface SEOSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItem: LocationCountry | LocationRegion | LocationCity | null;
}

interface SEOFormData {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
}

const SEOSettingsModal: React.FC<SEOSettingsModalProps> = ({
  isOpen,
  onClose,
  selectedItem
}) => {
  const dispatch = useAppDispatch();
  const { 
    locationSeos, 
    loading, 
    error 
  } = useAppSelector(state => state.locationSeos);

  const [seoFormData, setSeoFormData] = useState<SEOFormData>({
    title: "",
    description: "",
    keywords: "",
    canonicalUrl: "",
    ogTitle: "",
    ogDescription: "",
    ogImage: ""
  });

  const [isLoading, setIsLoading] = useState(false);
  const [existingSeoId, setExistingSeoId] = useState<string | null>(null);
  const [isFetchingSeo, setIsFetchingSeo] = useState(false);

  // Reset form when modal opens/closes or selectedItem changes
  useEffect(() => {
    if (isOpen && selectedItem) {
      // Reset form data first
      setSeoFormData({
        title: "",
        description: "",
        keywords: "",
        canonicalUrl: "",
        ogTitle: "",
        ogDescription: "",
        ogImage: ""
      });
      setExistingSeoId(null);
      
      // Check if SEO data already exists for this item
      checkExistingSEO();
    } else if (!isOpen) {
      // Reset form when modal closes
      setSeoFormData({
        title: "",
        description: "",
        keywords: "",
        canonicalUrl: "",
        ogTitle: "",
        ogDescription: "",
        ogImage: ""
      });
      setExistingSeoId(null);
    }
  }, [isOpen, selectedItem]);

  const checkExistingSEO = async () => {
    if (!selectedItem) return;

    setIsFetchingSeo(true);
    try {
      let result;
      let existingSeo = null;
      
      if ("initial" in selectedItem) {
        // Country
        result = await dispatch(fetchLocationSeosByCountry(selectedItem.id));
        if (result.payload && Array.isArray(result.payload)) {
          existingSeo = result.payload.find((seo: any) => seo.country?.id === selectedItem.id);
        }
      } else if ("country" in selectedItem && !("region" in selectedItem)) {
        // Region
        result = await dispatch(fetchLocationSeosByRegion(selectedItem.id));
        if (result.payload && Array.isArray(result.payload)) {
          existingSeo = result.payload.find((seo: any) => seo.region?.id === selectedItem.id);
        }
      } else if ("region" in selectedItem) {
        // City
        result = await dispatch(fetchLocationSeosByCity(selectedItem.id));
        if (result.payload && Array.isArray(result.payload)) {
          existingSeo = result.payload.find((seo: any) => seo.city?.id === selectedItem.id);
        }
      }

      if (existingSeo) {
        setExistingSeoId(existingSeo.id);
        setSeoFormData({
          title: existingSeo.metaTitle || "",
          description: existingSeo.metaDescription || "",
          keywords: existingSeo.keywords?.join(", ") || "",
          canonicalUrl: existingSeo.canonicalUrl || "",
          ogTitle: existingSeo.ogTitle || "",
          ogDescription: existingSeo.ogDescription || "",
          ogImage: existingSeo.ogImageUrl || ""
        });
      }
    } catch (error) {
      console.error("Error checking existing SEO:", error);
    } finally {
      setIsFetchingSeo(false);
    }
  };

  const handleSeoFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSeoFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSeoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;

    setIsLoading(true);
    dispatch(clearLocationSeoError());
    
    try {
      const seoData = {
        metaTitle: seoFormData.title,
        metaDescription: seoFormData.description,
        keywords: seoFormData.keywords.split(",").map(k => k.trim()).filter(k => k),
        canonicalUrl: seoFormData.canonicalUrl,
        ogTitle: seoFormData.ogTitle,
        ogDescription: seoFormData.ogDescription,
        ogImageUrl: seoFormData.ogImage
      };

      if (existingSeoId) {
        // Update existing SEO
        await dispatch(updateLocationSeo({ id: existingSeoId, data: seoData }));
      } else {
        // Create new SEO
        const createData: LocationSeoCreateDto = {
          ...seoData,
          ...(("initial" in selectedItem) ? { country: { id: selectedItem.id, name: selectedItem.name } } : {}),
          ...(("country" in selectedItem && !("region" in selectedItem)) ? { 
            region: { id: selectedItem.id, name: selectedItem.name },
            country: { id: selectedItem.country.id, name: selectedItem.country.name }
          } : {}),
          ...(("region" in selectedItem) ? { 
            city: { id: selectedItem.id, name: selectedItem.name },
            region: { id: selectedItem.region.id, name: selectedItem.region.name },
            country: { id: selectedItem.country.id, name: selectedItem.country.name }
          } : {})
        };
        await dispatch(createLocationSeo(createData));
      }
      
      onClose();
    } catch (error) {
      console.error("Error saving SEO settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!selectedItem) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:w-full sm:max-w-[760px] lg:max-w-[900px] max-h-[90vh] sm:max-h-[80vh] p-4 sm:p-6 lg:p-8">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl lg:text-2xl">SEO Settings for {selectedItem.name}</DialogTitle>
        </DialogHeader>
        {isFetchingSeo ? (
          <div className="py-10 flex flex-col items-center justify-center text-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-sm text-gray-600">Loading existing SEO settings...</p>
          </div>
        ) : (
        <form onSubmit={handleSeoSubmit} className="space-y-4 sm:space-y-6">
          <div className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto pr-1 sm:pr-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                <Input 
                  type="text" 
                  name="title" 
                  value={seoFormData.title} 
                  onChange={handleSeoFormChange} 
                  placeholder="Enter meta title" 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" 
                />
                <p className="text-xs text-gray-500 mt-1">Characters: {seoFormData.title.length}/60</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                <textarea 
                  name="description" 
                  value={seoFormData.description} 
                  onChange={handleSeoFormChange} 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-y" 
                  placeholder="Enter meta description" 
                />
                <p className="text-xs text-gray-500 mt-1">Characters: {seoFormData.description.length}/160</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
                <Input 
                  type="text" 
                  name="keywords" 
                  value={seoFormData.keywords} 
                  onChange={handleSeoFormChange} 
                  placeholder="Enter keywords, separated by commas" 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Canonical URL</label>
                <Input 
                  type="text" 
                  name="canonicalUrl" 
                  value={seoFormData.canonicalUrl} 
                  onChange={handleSeoFormChange} 
                  placeholder="Enter canonical URL" 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" 
                />
              </div>
              <div className="md:col-span-2 border-t pt-4 md:pt-6">
                <h3 className="font-semibold text-gray-900 mb-4 text-base sm:text-lg">Social Media Preview</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">OG Title</label>
                    <Input 
                      type="text" 
                      name="ogTitle" 
                      value={seoFormData.ogTitle} 
                      onChange={handleSeoFormChange} 
                      placeholder="Enter Open Graph title" 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" 
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">OG Description</label>
                    <textarea 
                      name="ogDescription" 
                      value={seoFormData.ogDescription} 
                      onChange={handleSeoFormChange} 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-y" 
                      placeholder="Enter Open Graph description" 
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">OG Image URL</label>
                    <Input 
                      type="text" 
                      name="ogImage" 
                      value={seoFormData.ogImage} 
                      onChange={handleSeoFormChange} 
                      placeholder="Enter Open Graph image URL" 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" 
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading || loading} className="w-full sm:w-auto text-sm">
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || loading} className="w-full sm:w-auto text-sm">
              {isLoading || loading ? "Saving..." : existingSeoId ? "Update SEO Settings" : "Save SEO Settings"}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SEOSettingsModal;